import { timer as observableTimer, from as observableFrom, Observable } from 'rxjs';

import {
  debounce,
  distinctUntilChanged,
  withLatestFrom,
  filter,
  map,
  switchMap,
} from 'rxjs/operators';
import { Injectable, Inject } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import * as actions from './storage-sync.actions';
import { storageSyncSelector } from '../reducers/index';
import { Location } from '@angular/common';
import { Router } from '@angular/router';

import { initialState } from '../auth';
import { environment } from '../../../environments/environment';

@Injectable()
export class StorageSyncEffects {
  /**
   * Effect to save the state to local storage.
   *
   * First checks to make sure the app is in a hydrated state to prevent
   * writing an empty object to storage.
   *
   * Then saves each key in the state to storage.
   */
  @Effect()
  saveStateToStorage = this.actions$.pipe(
    ofType(actions.REQUEST_SAVE_TO_STORAGE),
    withLatestFrom(this.store),
    filter(([action, state]) => state.storageSync.rehydrated === true),
    map(([action]) => {
      const payload = (<actions.RequestSaveToStorage>action).payload;
      Object.getOwnPropertyNames(payload).map((key) => {
        const stateSlice = payload[key];
        if (typeof stateSlice !== 'undefined') {
          this.window.localStorage.setItem(key, JSON.stringify(payload[key]));
        }
      });
    }),
    map((res) => {
      return new actions.ResponseSaveToStorage();
    })
  );

  /**
   * Rehydrates the state from storage.
   */
  @Effect()
  nativeStorageSync = this.actions$.pipe(
    ofType(actions.REQUEST_REHYDRATE),
    map((action) => {
      const rehydratedState = this.rehydrateApplicationState();
      return { type: actions.RESPONSE_REHYDRATED, payload: rehydratedState };
    })
  );

  @Effect()
  initNativeStorageSync = this.actions$.pipe(
    ofType('@ngrx/effects/init'),
    map((action) => new actions.RequestRehydrate())
  );

  constructor(
    private actions$: Actions,
    private store: Store<any>,
    @Inject('Window') private window: Window,
    private location: Location,
    private router: Router
  ) {
    // We use this selector to decide when to write changes, and what to write
    this.store
      .select(storageSyncSelector)
      .pipe(
        distinctUntilChanged(),
        debounce(() => observableTimer(2000))
      )
      .subscribe((stateChange) => {
        this.store.dispatch(new actions.RequestSaveToStorage(stateChange));
      });
  }
  /**
   * Utility function to parse JSON that contains dates into an object that
   * contains Date objects.
   * @param jsonData A json string that contains timestamps
   */
  private parseWithDates(jsonData: any) {
    return JSON.parse(jsonData, (key: any, value: any) => {
      const detectDate = /(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/;
      if (typeof value === 'string' && detectDate.test(value)) {
        return new Date(value);
      }
      if (key === 'loading') {
        return false;
      }
      return value;
    });
  }

  /**
   * Reloads the application state from storage.
   */
  private rehydrateApplicationState() {
    const keys = Object.keys(this.window.localStorage);
    const values = keys.map((key) => this.window.localStorage.getItem(key));
    return keys.reduce((acc, curr, idx) => {
      const stateSlice = values[idx];
      // Forgive me:
      // I tried many many ways to get these off the Angular router
      // but they just weren't being picked up. Plus, we want to make
      // sure they are picked up immediately to avoid race conditions
      // with routing based on auth etc.
      const path = this.location.path();
      const params = this.parseUrlQueryParams(path);
      if (stateSlice) {
        let parsed;
        try {
          parsed = this.parseWithDates(stateSlice);
        } catch (parseErr) {
          return acc;
        }
        let rehydrated = Object.assign({}, acc, { [curr]: parsed });
        if (environment.useKratos) {
          rehydrated = { ...rehydrated, auth: initialState };
        }
        if (params && params.userId && params.accessToken) {
          // Cleanup the query params which are no longer needed
          this.router.navigate(['.']);
          rehydrated = {
            ...rehydrated,
            auth: {},
          };
        }
        return rehydrated;
      }
      return acc;
    }, {});
  }

  parseUrlQueryParams(urlPath: string): { [key: string]: any } {
    if (urlPath.indexOf('?') >= 0) {
      const query = urlPath.slice(urlPath.indexOf('?') + 1);
      const queryParams = query.split('&').map((param) => param.split('='));
      return queryParams.reduce((prev, next) => {
        const mapped = {
          [next[0]]: next[1],
        };
        return {
          ...prev,
          ...mapped,
        };
      }, {});
    }
    return {};
  }
}
